// src/pages/PagesUtilisateurs/DashboardUsers/components/DashboardOverview.jsx

import React from "react";
import {
  FaBell,
  FaCalendarAlt,
  FaEnvelope,
  FaMoneyBillWave,
} from "react-icons/fa";

/* -------------------------------------------------------------------------- */
/*                                    Data                                    */
/* -------------------------------------------------------------------------- */

const CARDS = [
  {
    id: "notifications",
    label: "Notifications non lues",
    key: "notifications",
    valueKey: "unread",
    icon: FaBell,
    theme: {
      card: `
        border-blue-200
        bg-blue-50/70
        hover:border-blue-300
        hover:bg-blue-50
        dark:border-blue-500/20
        dark:bg-blue-500/5
        dark:hover:bg-blue-500/10
      `,
      accent: "border-t-[#013E8E]",
      icon: `
        bg-white
        text-[#013E8E]
        ring-blue-100
        dark:bg-blue-500/10
        dark:text-blue-300
        dark:ring-blue-500/20
      `,
      value: "text-[#013E8E] dark:text-blue-300",
    },
  },

  {
    id: "messages",
    label: "Messages non lus",
    key: "messages",
    valueKey: "unread",
    icon: FaEnvelope,
    theme: {
      card: `
        border-sky-200
        bg-sky-50/70
        hover:border-sky-300
        hover:bg-sky-50
        dark:border-sky-500/20
        dark:bg-sky-500/5
        dark:hover:bg-sky-500/10
      `,
      accent: "border-t-sky-500",
      icon: `
        bg-white
        text-sky-600
        ring-sky-100
        dark:bg-sky-500/10
        dark:text-sky-300
        dark:ring-sky-500/20
      `,
      value: "text-sky-700 dark:text-sky-300",
    },
  },

  {
    id: "payments",
    label: "Règlements à effectuer",
    key: "payments",
    valueKey: "total",
    icon: FaMoneyBillWave,
    theme: {
      card: `
        border-amber-200
        bg-amber-50/70
        hover:border-amber-300
        hover:bg-amber-50
        dark:border-amber-500/20
        dark:bg-amber-500/5
        dark:hover:bg-amber-500/10
      `,
      accent: "border-t-amber-500",
      icon: `
        bg-white
        text-amber-600
        ring-amber-100
        dark:bg-amber-500/10
        dark:text-amber-300
        dark:ring-amber-500/20
      `,
      value: "text-amber-700 dark:text-amber-300",
    },
  },

  {
    id: "events",
    label: "Évènements favoris",
    key: "events",
    valueKey: "total",
    icon: FaCalendarAlt,
    theme: {
      card: `
        border-emerald-200
        bg-emerald-50/70
        hover:border-emerald-300
        hover:bg-emerald-50
        dark:border-emerald-500/20
        dark:bg-emerald-500/5
        dark:hover:bg-emerald-500/10
      `,
      accent: "border-t-emerald-500",
      icon: `
        bg-white
        text-emerald-600
        ring-emerald-100
        dark:bg-emerald-500/10
        dark:text-emerald-300
        dark:ring-emerald-500/20
      `,
      value: "text-emerald-700 dark:text-emerald-300",
    },
  },
];

/* -------------------------------------------------------------------------- */
/*                              DashboardOverview                             */
/* -------------------------------------------------------------------------- */

export default function DashboardOverview({ counts, onSelect }) {
  return (
    <section className="w-full min-w-0 max-w-full">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-light uppercase tracking-[0.16em] text-slate-400">
            Vue d&apos;ensemble
          </p>

          <h2 className="mt-1 text-base font-semibold text-slate-900 dark:text-white sm:text-lg">
            Votre espace en un coup d&apos;œil
          </h2>
        </div>

        <p className="hidden text-xs text-slate-400 sm:block">
          Accès rapide
        </p>
      </div>

      <div className="grid w-full min-w-0 max-w-full grid-cols-2 gap-3 lg:grid-cols-4">
        {CARDS.map((card) => {
          const Icon = card.icon;
          const theme = card.theme;
          const value = Number(counts?.[card.key]?.[card.valueKey] || 0);

          return (
            <button
              key={card.id}
              type="button"
              onClick={() => onSelect?.(card.id)}
              className={`
                group
                min-w-0
                overflow-hidden
                rounded-[1.4rem]
                border border-t-4
                p-3
                text-left
                shadow-sm
                transition-all
                duration-200
                hover:-translate-y-0.5
                hover:shadow-md
                active:translate-y-0
                active:scale-[0.99]
                sm:p-4
                ${theme.card}
                ${theme.accent}
              `}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                {/* Icône */}
                <span
                  className={`
                    flex h-12 w-12 shrink-0
                    items-center justify-center
                    rounded-2xl
                    text-lg
                    shadow-sm
                    ring-1
                    sm:h-14 sm:w-14
                    sm:text-xl
                    ${theme.icon}
                  `}
                >
                  <Icon />
                </span>

                {/* Contenu */}
                <div className="min-w-0 flex-1">
                  <strong
                    className={`
                      block
                      text-3xl
                      font-bold
                      leading-none
                      tracking-tight
                      sm:text-[2rem]
                      ${theme.value}
                    `}
                  >
                    {value}
                  </strong>

                  <p className="mt-2 line-clamp-2 text-xs font-semibold leading-4 text-slate-800 dark:text-slate-100 sm:text-sm">
                    {card.label}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}